import { AdditionalServiceType, ContactPreferenceType, ServiceType, WorkType } from "generated/types/types";

export function MapToLine(stringArray: string[]) {
    return stringArray.map(x => {
        return { text: x };
    });
}

/**
 * A function which can be used to improve type safety. If you have a switch statement with various possibilities,
 * set the default case to call this. Then, when you add additional alternatives to the type being switched on,
 * the compiler will ensure that those alternatives are handled in the switch statement.
 * @param x a value which should never be passed
 */
export function assertNever(x: never): never {
    throw new Error("Unexpected object: " + x);
}

/**
 * Return x if it's non-null, otherwise throw an Error.
 * If possible, try to avoid using this function- instead, use sum types to explicitly encode the assumptions
 * the use of this function implies.
 *
 * For example, say at the call site we are making an assumption like "well, we know that this particular Vehicle
 * is valid, so the registration number must be non-null at this point in time". In this case, it would be better
 * to separate the state into two types, e.g. ValidVehicle, which has an explicitly non-null reg number, and
 * NonValidVehicle, which doesn't have a reg number field at all.
 *
 * @param x a value that may be null
 */
export function assertNotNull<T>(x: T | null): T {
    if (x === null) {
        throw Error("Not-null assertion failed");
    }
    return x;
}

export function assertNotNullOrUndefined<T>(x: T | null | undefined): T {
    if (x === null) {
        throw Error("Not-null assertion failed");
    }
    if (x === undefined) {
        throw Error("Not-undefined assertion failed");
    }
    return x;
}

/**
 * Check whether the given array contains the given object, comparing each array element's JSON representation to the
 * object's JSON representation.
 * @param ts an array of objects
 * @param t an object to look for
 */
export function containsObject<T extends object>(ts: T[], t: T): boolean {
    return ts.map(t => JSON.stringify(t)).includes(JSON.stringify(t));
}

export const getWindowWidth = () => {
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    return width;
};

export const getWindowHeight = () => {
    const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    return height;
};

export const ConvertToSNServiceIds = (requiredAdditionalServices?: AdditionalServiceType, requiredWork?: WorkType[]): ServiceType[] => {
    const snServiceIds: ServiceType[] = [];

    const converted = ConvertAdditionalService(requiredAdditionalServices);
    if (converted !== undefined && converted !== null) {
        snServiceIds.push(converted);
    }

    requiredWork?.forEach(x => {
        if (x === undefined) {
            return;
        }
        const converted = ConvertWork(x);
        if (converted !== undefined && converted !== null) {
            snServiceIds.push(converted);
        }
    });

    return snServiceIds;
};

export const AnyTruthyProperty = (object: object): boolean => {
    for (const [, value] of Object.entries(object)) {
        if (value) return true;
    }
    return false;
};

const ConvertWork = (work: WorkType): ServiceType => {
    switch (work) {
        case WorkType.Mot:
            return ServiceType.Mot;
        case WorkType.RepairOther:
            return ServiceType.Repair;
        case WorkType.Service:
            return ServiceType.Service;
        case WorkType.Tyres:
            return ServiceType.Tyres;
        default:
            assertNever(work);
    }
};

const ConvertAdditionalService = (service?: AdditionalServiceType): ServiceType | null => {
    switch (service) {
        case AdditionalServiceType.Courtesy:
            return ServiceType.CourtesyCar;
        case AdditionalServiceType.Delivery:
            return ServiceType.CollectionDelivery;
        case AdditionalServiceType.NoAdditional:
        case AdditionalServiceType.WhileUWait:
        case undefined:
            return null;
        default:
            assertNever(service);
    }
};

export const formattedContactPreference = (contactPreferences: ContactPreferenceType[]) => {
    return contactPreferences.map(contact => {
        if (contact === ContactPreferenceType.Sms) {
            return ContactPreferenceType.Sms.toUpperCase();
        }

        if (contact === ContactPreferenceType.Email) {
            return "e-mail";
        }

        return contact;
    }).join(" & ");
}
